/**
 * Adds an item to a priority array.
 *
 * If the item is faulty, returns the list unchanged.
 * If the item is already in the list, then move its first occurrence to the front.
 * Otherwise, remove items from the front until the list is of maximum specified size - 1 and add the item to the front.
 *
 * @param item - Generic item that we want to insert in a list.
 * @param priorityList - List in which to insert the item.
 * @param [maxItemsInList = 5] - Maximum number of items allowed in the list.
 */
export function updatePriorityList(item: string, priorityList: Array<string>, maxItemsInList = 5) {
  // Make a deep copy so that we don't affect the original array.
  let result = [...priorityList]

  // Faulty item. We just return the list without doing any changes.
  if (item === null || item === undefined) {
    return result
  }

  // Trim the item, so that there is no whitespace.
  item = item.trim()

  // Empty string. We just return the list without doing any changes.
  if (!item) {
    return result
  }

  // Check if the item was already searched.
  const searchItemIndex = result.findIndex((itemInList) => itemInList.trim() === item)

  // Item has been found.
  if (searchItemIndex >= 0) {
    // Item is in the list but not in the front, so we make it be in front.
    // If item is already in front we don't change the list.
    if (searchItemIndex !== result.length - 1) {
      const extractedItem = result.splice(searchItemIndex, 1)[0]
      result.push(extractedItem)
    }
  } else {
    // Remove items in the front until array is of max size - 1. Then add the search item to the front.
    // Values are negative so that it will do the slicing backwards - back to front.
    result = [...result.slice(-maxItemsInList + 1, priorityList.length), item]
  }

  return result
}
